import React, { useCallback, useMemo, useState } from 'react'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCopy, faEdit } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import DeleteIcon from '@material-ui/icons/Delete'
import ErrorIcon from '@material-ui/icons/Error'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { NonDismissibleDialog } from '@scoir/modals'
import { format } from 'date-fns'
import { forOwn } from 'lodash'

import { CollegeDocumentType, DOCUMENT_LABELS } from 'common/document.labels.type'

import { useRounds } from '../AppRoundProvider'
import { useDeleteRequirements } from '../RoundRequirementsProvider'
import { RoundRequirement } from '../types'
import RequirementsModalTrigger from './RequirementsModal'
import recommendationRequirementsForRound from './recommendation-summary'

const { Body, Footer } = NonDismissibleDialog

const useStyles = makeStyles((theme) => ({
  checkIcon: {
    color: theme.palette.success.main,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  expandBtn: {
    marginLeft: 'auto',
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  cardTypography: {
    fontSize: theme.typography.pxToRem(16),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  cardContentContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gridGap: theme.spacing(1),
  },
  chipContainer: {
    fontSize: theme.typography.pxToRem(14),
  },
}))

export const RequirementsCard = ({
  requirement,
  canConfigure,
}: {
  requirement: RoundRequirement
  canConfigure: boolean
}) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [hasDeleteError, setHasDeleteError] = useState(false)
  const classes = useStyles()

  const deleteRound = useDeleteRequirements()
  const rounds = useRounds()
  const confirmDelete = useCallback(() => {
    setHasDeleteError(false)
    deleteRound(requirement.id)
      .then(() => setIsConfirmingDelete(false))
      .catch(() => setHasDeleteError(true))
  }, [deleteRound, requirement.id])
  const [expanded, setExpanded] = React.useState(false)
  const handleExpandClick = () => {
    setExpanded(!expanded)
  }
  const linkedDeadlines = useMemo(
    () =>
      rounds
        .filter((round) => requirement.id === round.requirementsId)
        .map((round) => round.name)
        .join(', '),
    [rounds, requirement],
  )
  const supportingDocs = useMemo(() => {
    const required: string[] = []
    const asAvailable: string[] = []
    const doNotSend: string[] = []
    if (requirement.applicantType === 'firstYear' && requirement.supportingDocuments !== undefined) {
      forOwn(requirement.supportingDocuments, (value, key) => {
        if (value === 'Required' || value === 'PendingRequired')
          required.push(DOCUMENT_LABELS[key as CollegeDocumentType])
        if (value === 'AsAvailable') asAvailable.push(DOCUMENT_LABELS[key as CollegeDocumentType])
        if (value === 'DoNotSend') doNotSend.push(DOCUMENT_LABELS[key as CollegeDocumentType])
      })
    }
    if (requirement.applicantType === 'firstYear' && requirement.academicRecords !== undefined) {
      forOwn(requirement.academicRecords, (value, key) => {
        if (value === 'Required' || value === 'PendingRequired')
          required.push(DOCUMENT_LABELS[key as CollegeDocumentType])
        if (value === 'AsAvailable') asAvailable.push(DOCUMENT_LABELS[key as CollegeDocumentType])
        if (value === 'DoNotSend') doNotSend.push(DOCUMENT_LABELS[key as CollegeDocumentType])
      })
    }
    if (requirement.applicantType === 'transfer' && requirement.transferRequirements !== undefined) {
      forOwn(requirement.transferRequirements, (value, key) => {
        if (value === 'Required' || value === 'PendingRequired')
          required.push(DOCUMENT_LABELS[key as CollegeDocumentType])
        if (value === 'AsAvailable') asAvailable.push(DOCUMENT_LABELS[key as CollegeDocumentType])
        if (value === 'DoNotSend') doNotSend.push(DOCUMENT_LABELS[key as CollegeDocumentType])
      })
    }
    if (required.length === 0) required.push('none')
    if (asAvailable.length === 0) asAvailable.push('none')
    if (doNotSend.length === 0) doNotSend.push('none')
    return {
      required: required.join(', '),
      asAvailable: asAvailable.join(', '),
      doNotSend: doNotSend.join(', '),
    }
  }, [
    requirement.supportingDocuments,
    requirement.academicRecords,
    requirement.transferRequirements,
    requirement.applicantType,
  ])
  return (
    <>
      <Card>
        <CardHeader
          title={requirement.name}
          titleTypographyProps={{
            className: classes.cardTypography,
            variant: 'body1',
            color: 'textPrimary',
            component: 'h3',
          }}
        />
        <Divider />
        <CardContent>
          {requirement.updatedAt ? (
            <Box
              display="flex"
              alignItems="center"
            >
              <Box mr={1}>
                <CheckCircleIcon className={classes.checkIcon} />
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                component="p"
              >
                Last updated by {requirement.updatedByName} on{' '}
                {format(new Date(requirement.updatedAt), "M/d/yyyy h:mm aaaaa'm'")}
              </Typography>
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
            >
              <Box mr={1}>
                <ErrorIcon className={classes.warningIcon} />
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                component="p"
              >
                This round has not been updated recently
              </Typography>
            </Box>
          )}
          <Collapse
            in={expanded}
            timeout="auto"
          >
            <Box pt={1}>
              <Box>
                <Typography>
                  <strong>Requirements</strong>
                </Typography>
                <Typography>{recommendationRequirementsForRound(requirement)}</Typography>
              </Box>
              <Box mt={2}>
                <Typography>
                  <strong>Documents</strong>
                </Typography>
                <ul>
                  <li>
                    <Typography>Required: {supportingDocs.required}</Typography>
                  </li>
                  <li>
                    <Typography>As Available: {supportingDocs.asAvailable}</Typography>
                  </li>
                  <li>
                    <Typography>Do Not Send: {supportingDocs.doNotSend}</Typography>
                  </li>
                </ul>
              </Box>
            </Box>
            <Box
              mt={1}
              className={classes.cardContentContainer}
            >
              <Chip
                size="small"
                color={requirement.applicantType === 'firstYear' ? 'primary' : 'secondary'}
                label={requirement.applicantType === 'firstYear' ? 'First-Year Applicants' : 'Transfer Applicants'}
              />
            </Box>
          </Collapse>
        </CardContent>
        <Divider />
        <CardActions disableSpacing>
          <RequirementsModalTrigger
            disabled={!canConfigure}
            requirements={requirement}
            Component={IconButton}
            aria-label={`Edit Requirements ${requirement.name}`}
            color="primary"
            variant="contained"
          >
            <FontAwesomeIcon icon={faEdit as IconProp} />
          </RequirementsModalTrigger>
          <RequirementsModalTrigger
            copy
            disabled={!canConfigure}
            requirements={requirement}
            Component={IconButton}
            aria-label={`Copy Requirements ${requirement.name}`}
            color="primary"
            variant="contained"
          >
            <FontAwesomeIcon icon={faCopy as IconProp} />
          </RequirementsModalTrigger>
          <IconButton
            disabled={!canConfigure}
            color="primary"
            aria-label={`Delete Requirements ${requirement.name}`}
            onClick={() => {
              setIsConfirmingDelete(true)
            }}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            className={`${classes.expandBtn} ${expanded ? classes.expandOpen : classes.expand}`}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Hide' : 'Show'} ${requirement.name} content`}
          >
            <ExpandMoreIcon />
          </IconButton>
        </CardActions>
      </Card>
      <NonDismissibleDialog
        open={!!isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        scroll="paper"
        headerText="Confirm Delete Requirements"
      >
        <Body>
          <Typography>Are your sure you want to delete {requirement.name}?</Typography>
          {hasDeleteError && (
            <Box
              my={2}
              textAlign="center"
            >
              <Typography
                role="alert"
                color="error"
              >
                An error occurred while deleting your requirements, please try again another time.
              </Typography>
            </Box>
          )}
          {linkedDeadlines && (
            <Box
              my={2}
              textAlign="center"
            >
              <Typography
                role="alert"
                color="error"
              >
                Unable to delete. There are Deadline(s) that depend on this Requirement:&nbsp;
                {linkedDeadlines}
              </Typography>
            </Box>
          )}
        </Body>
        <Footer>
          <Footer.CloseButton>Cancel</Footer.CloseButton>
          <Footer.PrimaryActionButton
            onClick={() => confirmDelete()}
            disabled={!!linkedDeadlines}
          >
            Confirm
          </Footer.PrimaryActionButton>
        </Footer>
      </NonDismissibleDialog>
    </>
  )
}
export const RequirementsGrid = ({
  requirements,
  canConfigure,
}: {
  requirements: Array<RoundRequirement>
  canConfigure: boolean
}) => (
  <Grid
    container
    spacing={2}
  >
    {requirements.map((requirement) => (
      <Grid
        item
        key={requirement.id}
        xs={12}
        sm={6}
        lg={4}
      >
        <RequirementsCard
          requirement={requirement}
          canConfigure={canConfigure}
        />
      </Grid>
    ))}
  </Grid>
)
